import { useState } from 'react';
import {
  BookOpen,
  Phone,
  MessageSquare,
  Settings,
  Webhook,
  Key,
  CheckCircle,
  ArrowRight,
  ExternalLink,
  Copy,
  Zap,
  AlertTriangle,
  HelpCircle,
  Code
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@easyflow/ui-frontend/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-frontend/components/ui/tabs';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@easyflow/ui-frontend/components/ui/alert';
import { useToast } from '@/hooks/use-toast';

export default function MessagingDocs() {
  const { toast } = useToast();
  const [activeSection, setActiveSection] = useState('quickstart');

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copiato negli appunti' });
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Documentazione Messaggistica</h1>
        <p className="text-muted-foreground">Guida completa per configurare e utilizzare SMS e WhatsApp con Twilio</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-1">
          <Card className="sticky top-6">
            <CardHeader>
              <CardTitle className="text-base">Indice</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1">
              {[
                { id: 'quickstart', label: 'Quick Start', icon: Zap },
                { id: 'setup', label: 'Configurazione', icon: Settings },
                { id: 'sms', label: 'SMS', icon: MessageSquare },
                { id: 'whatsapp', label: 'WhatsApp', icon: Phone },
                { id: 'webhooks', label: 'Webhook', icon: Webhook },
                { id: 'ai-integration', label: 'Integrazione AI', icon: Code },
                { id: 'troubleshooting', label: 'Risoluzione Problemi', icon: HelpCircle }
              ].map((item) => (
                <Button
                  key={item.id}
                  variant={activeSection === item.id ? 'secondary' : 'ghost'}
                  className="w-full justify-start"
                  onClick={() => setActiveSection(item.id)}
                >
                  <item.icon className="h-4 w-4 mr-2" />
                  {item.label}
                </Button>
              ))}
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-3 space-y-6">
          {activeSection === 'quickstart' && (
            <>
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-yellow-500" />
                    <CardTitle>Quick Start</CardTitle>
                  </div>
                  <CardDescription>Inizia a inviare messaggi in 5 minuti</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold">1</div>
                      <div>
                        <h4 className="font-medium">Crea un account Twilio</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Registrati su <a href="https://www.twilio.com/try-twilio" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">twilio.com</a> (gratuito, ricevi crediti di prova)
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold">2</div>
                      <div>
                        <h4 className="font-medium">Ottieni le credenziali API</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Dalla Console Twilio, copia l'Account SID e crea una API Key
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold">3</div>
                      <div>
                        <h4 className="font-medium">Configura le credenziali</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Vai su Impostazioni → Credenziali e inserisci i dati
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center font-bold">4</div>
                      <div>
                        <h4 className="font-medium">Acquista un numero</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Dalla Console Twilio, acquista un numero con capacità SMS
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center font-bold">
                        <CheckCircle className="h-4 w-4" />
                      </div>
                      <div>
                        <h4 className="font-medium">Inizia a comunicare!</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Usa la sezione Chat per inviare messaggi o lascia che gli AI agent lo facciano automaticamente
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Alert className="bg-blue-50 border-blue-200">
                <Zap className="h-4 w-4 text-blue-600" />
                <AlertTitle className="text-blue-900">Crediti di prova</AlertTitle>
                <AlertDescription className="text-blue-700">
                  Twilio offre crediti gratuiti ($15+) ai nuovi account per testare i servizi. 
                  Durante il trial, puoi inviare messaggi solo a numeri verificati.
                </AlertDescription>
              </Alert>
            </>
          )}

          {activeSection === 'setup' && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  <CardTitle>Configurazione Completa</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-semibold text-lg mb-3">1. Creare un Account Twilio</h3>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                    <li>Vai su <a href="https://www.twilio.com/try-twilio" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">twilio.com/try-twilio</a></li>
                    <li>Compila il form di registrazione con email e password</li>
                    <li>Verifica la tua email e il numero di telefono</li>
                    <li>Rispondi alle domande iniziali (puoi saltarle)</li>
                  </ol>
                </div>

                <div>
                  <h3 className="font-semibold text-lg mb-3">2. Ottenere Account SID</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    L'Account SID si trova nella dashboard principale della Console Twilio, in alto a sinistra.
                  </p>
                  <div className="bg-gray-100 rounded p-3 font-mono text-sm">
                    Account SID: ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-lg mb-3">3. Creare API Key</h3>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                    <li>Nella Console Twilio, vai su Account → API keys & tokens</li>
                    <li>Clicca "Create API key"</li>
                    <li>Scegli un nome descrittivo (es. "EDGVoIP Production")</li>
                    <li>Seleziona "Standard" come tipo di key</li>
                    <li>Clicca "Create API Key"</li>
                    <li><strong>IMPORTANTE:</strong> Copia subito SID e Secret, il Secret non sarà più visibile!</li>
                  </ol>
                  <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-3">
                    <p className="text-sm text-yellow-800">
                      <AlertTriangle className="h-4 w-4 inline mr-1" />
                      <strong>Salva l'API Secret immediatamente!</strong> Twilio lo mostra solo una volta. 
                      Se lo perdi, dovrai creare una nuova API Key.
                    </p>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-lg mb-3">4. Inserire le credenziali</h3>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                    <li>Vai su Comunicazione → Impostazioni → Credenziali</li>
                    <li>Inserisci l'Account SID (inizia con AC)</li>
                    <li>Inserisci l'API Key SID (inizia con SK)</li>
                    <li>Inserisci l'API Secret</li>
                    <li>Clicca "Salva Credenziali"</li>
                    <li>Clicca "Testa Connessione" per verificare</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          )}

          {activeSection === 'sms' && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  <CardTitle>Configurazione SMS</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-semibold text-lg mb-3">Acquistare un Numero SMS</h3>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                    <li>Nella Console Twilio, vai su Phone Numbers → Manage → Buy a number</li>
                    <li>Seleziona il paese (es. Italia, USA)</li>
                    <li>Assicurati che "SMS" sia selezionato nelle capabilities</li>
                    <li>Scegli un numero e clicca "Buy"</li>
                    <li>Conferma l'acquisto (costo ~$1/mese per numeri USA)</li>
                  </ol>
                </div>

                <div>
                  <h3 className="font-semibold text-lg mb-3">Numeri Italiani</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Per numeri italiani (+39), Twilio richiede documentazione aggiuntiva:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                    <li>Documento d'identità del rappresentante legale</li>
                    <li>Prova di indirizzo dell'azienda</li>
                    <li>Registrazione camera di commercio</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold text-lg mb-3">A2P 10DLC (USA)</h3>
                  <p className="text-sm text-muted-foreground">
                    Per inviare SMS a numeri USA in produzione, è necessario registrare il brand e la campagna:
                  </p>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground mt-2">
                    <li>Registra il tuo Brand (azienda)</li>
                    <li>Crea una Messaging Campaign</li>
                    <li>Attendi l'approvazione (1-3 giorni lavorativi)</li>
                  </ol>
                  <Button variant="link" className="p-0 h-auto" asChild>
                    <a href="https://support.twilio.com/hc/en-us/articles/1260801864489" target="_blank" rel="noopener noreferrer">
                      Guida completa A2P 10DLC <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {activeSection === 'whatsapp' && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-green-500" />
                  <CardTitle>Configurazione WhatsApp</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <Alert className="bg-green-50 border-green-200">
                  <MessageSquare className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-700">
                    WhatsApp Business API richiede l'approvazione di Meta. Il processo può richiedere alcuni giorni.
                  </AlertDescription>
                </Alert>

                <div>
                  <h3 className="font-semibold text-lg mb-3">Sandbox di Test</h3>
                  <p className="text-sm text-muted-foreground mb-2">
                    Per testare WhatsApp senza approvazione, usa la Sandbox di Twilio:
                  </p>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                    <li>Nella Console Twilio, vai su Messaging → Try it out → Send a WhatsApp message</li>
                    <li>Segui le istruzioni per connettere il tuo telefono alla Sandbox</li>
                    <li>Invia un messaggio con il codice univoco al numero indicato</li>
                    <li>Ora puoi inviare e ricevere messaggi di test</li>
                  </ol>
                </div>

                <div>
                  <h3 className="font-semibold text-lg mb-3">Produzione (Business Profile)</h3>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                    <li>Crea un account Facebook Business Manager</li>
                    <li>Nella Console Twilio, vai su Messaging → Senders → WhatsApp senders</li>
                    <li>Clicca "Register a WhatsApp Business Profile"</li>
                    <li>Collega il tuo Facebook Business Manager</li>
                    <li>Compila i dettagli dell'azienda</li>
                    <li>Attendi la verifica di Meta (1-5 giorni)</li>
                  </ol>
                </div>

                <div>
                  <h3 className="font-semibold text-lg mb-3">Template Messages</h3>
                  <p className="text-sm text-muted-foreground">
                    WhatsApp richiede template pre-approvati per i messaggi proattivi (non di risposta). 
                    I template devono essere creati e approvati nella Console Twilio o nel Business Manager di Meta.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {activeSection === 'webhooks' && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Webhook className="h-5 w-5" />
                  <CardTitle>Configurazione Webhook</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  I webhook permettono di ricevere notifiche quando arrivano messaggi o quando lo stato di consegna cambia.
                </p>

                <div>
                  <h3 className="font-semibold text-lg mb-3">Configurare Webhook per SMS</h3>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                    <li>Nella Console Twilio, vai su Phone Numbers → Manage → Active numbers</li>
                    <li>Clicca sul numero che vuoi configurare</li>
                    <li>Scorri fino a "Messaging Configuration"</li>
                    <li>In "A MESSAGE COMES IN", seleziona "Webhook" e inserisci l'URL</li>
                    <li>In "STATUS CALLBACK URL", inserisci l'URL per gli aggiornamenti di stato</li>
                    <li>Salva le modifiche</li>
                  </ol>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium mb-2">URL Webhook da usare:</h4>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">SMS in arrivo:</p>
                      <div className="flex gap-2">
                        <code className="bg-white px-3 py-1 rounded border text-sm flex-1">
                          {window.location.origin}/api/webhooks/twilio/sms
                        </code>
                        <Button variant="outline" size="sm" onClick={() => copyToClipboard(`${window.location.origin}/api/webhooks/twilio/sms`)}>
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Status callback:</p>
                      <div className="flex gap-2">
                        <code className="bg-white px-3 py-1 rounded border text-sm flex-1">
                          {window.location.origin}/api/webhooks/twilio/status
                        </code>
                        <Button variant="outline" size="sm" onClick={() => copyToClipboard(`${window.location.origin}/api/webhooks/twilio/status`)}>
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {activeSection === 'ai-integration' && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Code className="h-5 w-5" />
                  <CardTitle>Integrazione con AI Agent</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  Gli AI agent possono inviare SMS e WhatsApp automaticamente usando i tool MCP di Twilio.
                </p>

                <div>
                  <h3 className="font-semibold text-lg mb-3">Tool Disponibili</h3>
                  <div className="space-y-3">
                    <div className="border rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge>twilio_send_sms</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">Invia un messaggio SMS a un numero di telefono</p>
                    </div>
                    <div className="border rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge>twilio_send_whatsapp</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">Invia un messaggio WhatsApp</p>
                    </div>
                    <div className="border rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge>twilio_list_messages</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">Elenca i messaggi recenti</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-lg mb-3">Abilitare i Tool per un Agent</h3>
                  <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                    <li>Vai su Impostazioni Sviluppatore → MCP → Integrazioni Template</li>
                    <li>Configura e attiva l'integrazione Twilio</li>
                    <li>Vai su AI Tools → Agents</li>
                    <li>Modifica l'agent desiderato</li>
                    <li>Nella sezione "Tool Bindings", abilita i tool Twilio</li>
                    <li>Salva l'agent</li>
                  </ol>
                </div>

                <Alert>
                  <Zap className="h-4 w-4" />
                  <AlertTitle>Esempio d'uso</AlertTitle>
                  <AlertDescription>
                    <p className="mt-2">Prompt all'agent:</p>
                    <code className="block mt-1 bg-gray-100 p-2 rounded text-sm">
                      "Invia un SMS di conferma prenotazione al numero +39xxxxxxxxxx con i dettagli della camera"
                    </code>
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          )}

          {activeSection === 'troubleshooting' && (
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5" />
                  <CardTitle>Risoluzione Problemi</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h3 className="font-semibold text-lg mb-3">Errori Comuni</h3>
                  <div className="space-y-4">
                    <div className="border-l-4 border-red-500 pl-4">
                      <p className="font-medium">Error 21608: The "To" phone number is not currently reachable</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Il numero destinatario non è raggiungibile. Verifica che sia un numero valido e attivo.
                      </p>
                    </div>

                    <div className="border-l-4 border-red-500 pl-4">
                      <p className="font-medium">Error 21211: The "From" phone number is not a valid phone number</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Il numero mittente non è valido. Assicurati di usare un numero acquistato sul tuo account Twilio.
                      </p>
                    </div>

                    <div className="border-l-4 border-red-500 pl-4">
                      <p className="font-medium">Error 20003: Authentication Error</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Credenziali non valide. Verifica Account SID, API Key SID e API Secret.
                      </p>
                    </div>

                    <div className="border-l-4 border-yellow-500 pl-4">
                      <p className="font-medium">Messaggi non ricevuti</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Verifica che i webhook siano configurati correttamente e che l'URL sia raggiungibile pubblicamente.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold text-lg mb-3">Link Utili</h3>
                  <div className="space-y-2">
                    <Button variant="link" className="p-0 h-auto" asChild>
                      <a href="https://www.twilio.com/docs/errors" target="_blank" rel="noopener noreferrer">
                        Elenco completo errori Twilio <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </Button>
                    <br />
                    <Button variant="link" className="p-0 h-auto" asChild>
                      <a href="https://support.twilio.com/" target="_blank" rel="noopener noreferrer">
                        Supporto Twilio <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </Button>
                    <br />
                    <Button variant="link" className="p-0 h-auto" asChild>
                      <a href="https://www.twilio.com/docs/usage/api/usage-record" target="_blank" rel="noopener noreferrer">
                        Documentazione Usage Records API <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
